import { DeprecatedAvailableUpdatesIcon, IconButton, ProgressBar, SuccessIcon } from '@lokalise/louis';

import {
    type FileUpload,
    isFileUploadRemoving,
    isFileUploadStaged,
    isFileUploadSuccess,
    isFileUploadUploading,
    matchFileUpload,
} from '../../../model/fileUpload';
import splitFileName from '../../../utils/splitFileName';
import StyleGuideGridRow from '../StyleGuideGridRow';
import { IconPlaceholder } from '../styles';
import { UploadErrorMessage, UploadProgressBarWrapper, UploadRemovingMessage } from './styles';

const getProgressForFileUpload = (fileUpload: FileUpload) => {
    if (isFileUploadUploading(fileUpload)) {
        return fileUpload.progressPercent;
    }

    return isFileUploadSuccess(fileUpload) || isFileUploadStaged(fileUpload) ? 100 : 0;
};

type FileUploadItemProps = {
    hidden: boolean;
    fileUpload: FileUpload;
    onFileUploadRemoved: (fileUpload: FileUpload) => void;
};

const FileUploadItem = ({ fileUpload, hidden, onFileUploadRemoved }: FileUploadItemProps) => (
    <StyleGuideGridRow
        readonly
        deletable
        hidden={hidden}
        name={isFileUploadStaged(fileUpload) ? fileUpload.name : fileUpload.file.name}
        onDelete={() => onFileUploadRemoved(fileUpload)}
        removing={isFileUploadRemoving(fileUpload)}
    >
        {matchFileUpload(fileUpload, {
            pendingCase: () => (
                <UploadProgressBarWrapper $pending>
                    <ProgressBar progress={0} className="style-guide-upload-progress" />
                    <IconPlaceholder />
                </UploadProgressBarWrapper>
            ),
            uploadingCase: (upload) => (
                <UploadProgressBarWrapper>
                    <ProgressBar progress={getProgressForFileUpload(upload)} className="style-guide-upload-progress" />
                    <IconPlaceholder />
                </UploadProgressBarWrapper>
            ),
            removedCase: () => null,
            removingCase: (upload) => {
                const { name } = splitFileName(upload.name ?? upload.file.name);

                return <UploadRemovingMessage>Removing {name}...</UploadRemovingMessage>;
            },
            errorCase: ({ error, retry }) => (
                <UploadErrorMessage>
                    {error}{' '}
                    {retry && (
                        <IconButton ariaLabel="Retry upload" onClick={retry}>
                            <DeprecatedAvailableUpdatesIcon />
                        </IconButton>
                    )}
                </UploadErrorMessage>
            ),
            successCase: () => (
                <UploadProgressBarWrapper $success>
                    <ProgressBar progress={100} className="style-guide-upload-progress" />
                    <SuccessIcon ariaLabel="Style guide uploaded successfully" />
                </UploadProgressBarWrapper>
            ),
            stagedCase: () => (
                <UploadProgressBarWrapper $success>
                    <ProgressBar progress={100} className="style-guide-upload-progress" />
                    <SuccessIcon ariaLabel="Style guide uploaded successfully" />
                </UploadProgressBarWrapper>
            ),
        })}
    </StyleGuideGridRow>
);

export default FileUploadItem;
